/**
 * Paths that must never be used as a post-login redirect target — they
 * either trigger another auth flow or loop back through the redirect
 * machinery. Listed here as prefixes; both exact match and `prefix + '/'`
 * forms are blocked.
 */
const FORBIDDEN_PREFIXES = ['/login', '/auth', '/access-denied'] as const;

/**
 * Validates a candidate redirect target captured from a query string or
 * sessionStorage. Returns the target unchanged if it is a same-origin
 * relative path that is safe to navigate to, otherwise null. Guards
 * against open-redirect, host smuggling, and login/callback loops.
 */
export function safeRedirectTarget(raw: string | undefined | null): string | null {
  if (!raw) return null;
  if (typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  // Protocol-relative URL (`//evil.com`) — would navigate cross-origin
  if (raw.startsWith('//')) return null;
  // Backslash variants are normalized to `/` by some browsers
  if (raw.includes('\\')) return null;

  // Resolve against the current origin and confirm it stays same-origin.
  let parsed: URL;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (parsed.origin !== window.location.origin) return null;

  // Reject self-referential auth paths to avoid redirect loops.
  const pathname = parsed.pathname;
  for (const prefix of FORBIDDEN_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return null;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
